/**
 * Página de Tipos de Documento do CRM
 * 
 * CRUD de tipos de documento exigidos em contratos
 * (RG, CNH, Comprovante de Endereço, CNPJ, etc.)
 * 
 * v1.1.0 - Validação de permissões granulares (canCreate, canEdit, canDelete)
 * @release v1.0.0 - 2026-04-09
 */

import { useState, useMemo } from 'react';
import {
  FileCheck,
  Plus,
  Search,
  Edit,
  Trash2,
  MoreHorizontal,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDocumentTypes, type DocumentType, type DocumentTypeInput } from '@/hooks/useDocumentTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { DocumentTypeFormDialog } from '@/components/contracts/DocumentTypeFormDialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/** Chave de permissão dedicada para esta página */
const MENU_KEY = 'crm_document_types';

/** Labels de categoria */
const CATEGORY_LABELS: Record<string, string> = {
  geral: 'Geral',
  identificacao: 'Identificação',
  endereco: 'Endereço',
  financeiro: 'Financeiro',
  juridico: 'Jurídico',
  outro: 'Outro',
};

export default function CrmDocumentTypes() {
  // Estado local
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingType, setEditingType] = useState<DocumentType | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Hook de permissões — controle granular por ação
  const { canCreate, canEdit, canDelete } = usePermissions();

  // Hook de dados
  const {
    documentTypes,
    isLoading,
    createDocumentType,
    updateDocumentType,
    deleteDocumentType,
  } = useDocumentTypes();

  // Filtragem
  const filteredTypes = useMemo(() => {
    return documentTypes.filter((dt) => {
      const matchesSearch =
        !search ||
        dt.name.toLowerCase().includes(search.toLowerCase()) ||
        dt.description?.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || dt.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [documentTypes, search, categoryFilter]);

  // Handlers
  const handleCreate = () => {
    setEditingType(null);
    setFormOpen(true);
  };

  const handleEdit = (dt: DocumentType) => {
    setEditingType(dt);
    setFormOpen(true);
  };

  const handleSave = (input: DocumentTypeInput & { id?: string }) => {
    if (input.id) {
      updateDocumentType.mutate(
        { ...input, id: input.id },
        { onSuccess: () => setFormOpen(false) }
      );
    } else {
      createDocumentType.mutate(input, {
        onSuccess: () => setFormOpen(false),
      });
    }
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteDocumentType.mutate(deleteId, {
        onSuccess: () => setDeleteId(null),
      });
    }
  };

  // Flags de permissão derivadas
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);

  // Verifica se o dropdown de ações deve ser exibido (ao menos uma ação disponível)
  const hasAnyAction = hasEditPermission || hasDeletePermission;

  return (
    <div className="flex flex-col h-full">
      {/* Header — padrão compacto */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Tipos de Documento</h1>
        </div>
        {hasCreatePermission && (
          <Button size="xs" onClick={handleCreate} className="shrink-0">
            <Plus className="h-4 w-4 mr-1.5" />
            Novo Tipo
          </Button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar tipos de documento..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40 h-8 text-sm">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredTypes.length === 0 ? (
          /* Empty state */
          <Card className="flex flex-col items-center justify-center py-16 px-8 text-center">
            <div className="p-4 rounded-full bg-muted mb-4">
              <FileCheck className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {search || categoryFilter !== 'all'
                ? 'Nenhum tipo encontrado'
                : 'Crie seu primeiro tipo de documento'}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mb-6">
              {search || categoryFilter !== 'all'
                ? 'Tente ajustar os filtros de busca.'
                : 'Tipos de documento são usados para definir quais anexos são exigidos em cada contrato. Ex: RG, CNH, Comprovante de Endereço.'}
            </p>
            {/* Botão empty state — visível somente com permissão de criação */}
            {!search && categoryFilter === 'all' && hasCreatePermission && (
              <Button onClick={handleCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                Criar Tipo de Documento
              </Button>
            )}
          </Card>
        ) : (
          /* Tabela */
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Atualizado</TableHead>
                  {hasAnyAction && <TableHead className="w-12" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.map((dt) => (
                  <TableRow key={dt.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{dt.name}</p>
                        {dt.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-xs">
                            {dt.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {CATEGORY_LABELS[dt.category] || dt.category}
                      </Badge>
                    </TableCell>
                    {/* Status — Badge outline + StatusDot (padrão Modelos de Contratos) */}
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                      >
                        <StatusDot tone={dt.is_active ? 'emerald' : 'muted'} />
                        {dt.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(dt.updated_at), "dd/MM/yy HH:mm", { locale: ptBR })}
                      </span>
                    </TableCell>
                    {/* Menu de ações — exibido somente se o usuário tem ao menos uma permissão */}
                    {hasAnyAction && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Editar — visível somente com permissão */}
                            {hasEditPermission && (
                              <DropdownMenuItem onClick={() => handleEdit(dt)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {/* Excluir — visível somente com permissão */}
                            {hasDeletePermission && (
                              <DropdownMenuItem
                                onClick={() => setDeleteId(dt.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Dialog de formulário */}
      <DocumentTypeFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        documentType={editingType}
        onSave={handleSave}
        isSaving={createDocumentType.isPending || updateDocumentType.isPending}
      />

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir tipo de documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Tipos vinculados a contratos publicados ou documentos de clientes não poderão ser excluídos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
